import { loadImage } from "../utils/image"
import { permute, slugify } from "../utils/string"

const MIN_LOADING_TIME = 300

const productGalleryMixin = {
    data: {
        isGalleryLoading: true,
        galleryImages: [],
        galleryPreviews: []
    },

    computed: {},

    watch: {},

    created() {
        // Check module instance
        if (!this.moduleInstance) return console.log("[beforeCreate] - Please store module instance 'this' into 'this.moduleInstance'")

        // Bind data
        const productTitle = this.moduleInstance.productTitle
        const productVariants = this.moduleInstance.productVariants
        const productCurrentVariantId = this.moduleInstance.productCurrentVariantId
        const productImages = this.moduleInstance.productImages

        // Prepare array to store loaded images
        this.loadedImages = []
        this.isFirstLoading = true

        // 1 - Get images list and slugify alt attribute
        this.setProductImagesData(productImages)

        // 2 - Get products variants, slugify options then create for each variant an array of images
        this.allProductVariants = this.setProductVariantsData(productVariants, productTitle, productImages)

        // 3 - Set current product variant
        this.currentProductVariant = this.allProductVariants.find(variant => variant.id === productCurrentVariantId)

        // 4 - Set gallery
        this.updateGallery()
    },

    beforeDestroy() {
        this.loadingTimeout && clearTimeout(this.loadingTimeout)
    },

    methods: {
        /**
        * Override productImages to add a slugify id and add HTTP protocol to create image link
        */
        setProductImagesData(images) {
            const httpProtocol = window.location.protocol

            let imagesIndex = 0
            while (imagesIndex < images.length) {

                const currentImageObj = images[imagesIndex]

                currentImageObj.id = slugify(currentImageObj.rawId.trim())
                currentImageObj.preview = httpProtocol + currentImageObj.preview
                currentImageObj.image = httpProtocol + currentImageObj.image

                imagesIndex++
            }
        },

        /**
        * Set new array of product variants
        */
        setProductVariantsData(variants, productTitle, productImages) {
            const newVariantsModelArr = []
            let variantsIndex = 0

            // Prepare array for images used by variants
            let usedImages = []

            while (variantsIndex < variants.length) {
                // Get current variant
                const { id } = variants[variantsIndex]

                // New object model
                const newVariantObj = {
                    id,
                    optionsImagesIds: [],
                    images: []
                }

                // 1 - Create array of all options combinations slugs
                const optionsValues =  variants[variantsIndex].options.map((value, index) => {
                    return slugify(value.trim())
                })
                const optionsCombinations = permute(optionsValues)
                const optionsCombinationsIds = optionsCombinations.map((arr, index) => {
                    return slugify(arr.join('-').trim())
                })
                newVariantObj.optionsImagesIds = [...optionsValues, ...optionsCombinationsIds]

                // 2 - add variant images
                newVariantObj.images = productImages.filter(images => newVariantObj.optionsImagesIds.indexOf(images.id) != -1);

                // 3 - store images used
                if (newVariantObj.images.length) {
                    usedImages = [...usedImages, ...newVariantObj.images]
                }

                // 4 - then store new object
                newVariantsModelArr.push(newVariantObj)

                variantsIndex++
            }

            // Remove duplicates
            const uniqUsedImages = [...new Set(usedImages)]

            // Get unused images : it's the common images
            const onlyInA = productImages.filter(this.compareImagesArr(uniqUsedImages));
            const onlyInB = uniqUsedImages.filter(this.compareImagesArr(productImages));
            const unusedImages = onlyInA.concat(onlyInB)

            // Then add common images for each variants
            if (unusedImages.length) {
                newVariantsModelArr.forEach((variant) => {
                    variant.images = [...variant.images, ...unusedImages]
                })
            }

            return newVariantsModelArr
        },

        /**
        * Load and populate gallery images
        */
        async updateGallery() {
            const currentProductImages = this.currentProductVariant.images

            this.loadingTimeout && clearTimeout(this.loadingTimeout)

            // // Set loading state
            // this.isGalleryLoading = true

            // Get all product images url
            const images = []
            const previews = []
            let index = 0
            while (index < currentProductImages.length) {
                const currentProductImagesObj = currentProductImages[index]
                previews.push(currentProductImagesObj.preview)
                images.push(currentProductImagesObj.image)
                index++
            }

            // New images to load
            const loadedImagesIds = this.loadedImages.map(imageObj => imageObj.id)
            const imagesToLoad = images.filter(imageUrl => loadedImagesIds.indexOf(imageUrl) === -1)
            const previewsToLoad = previews.filter(imageUrl => loadedImagesIds.indexOf(imageUrl) === -1)
            const allImagesToLoad = [...imagesToLoad, ...previewsToLoad]

            // console.group("IMAGES TO LOAD");
            // console.log("Images", imagesToLoad);
            // console.log("Previews", previewsToLoad);
            // console.groupEnd();

            if (allImagesToLoad.length) {
                // Set loading state
                this.isGalleryLoading = true
            }

            if (imagesToLoad.length) {
                await this.loadImages(imagesToLoad)
            }

            if (previewsToLoad.length) {
                await this.loadImages(previewsToLoad)
            }


            this.loadingTimeout = setTimeout(() => {

                // Set gallery images / previews
                this.galleryImages = []
                this.galleryPreviews = []

                currentProductImages.forEach(image => {
                    const targetImage = this.loadedImages.find(loadedImage => loadedImage.id === image.image)
                    const targetPreview = this.loadedImages.find(loadedImage => loadedImage.id === image.preview)

                    this.galleryImages.push(targetImage)
                    this.galleryPreviews.push(targetPreview)
                })

                // Call hook
                this.onUpdateGallery?.()

                // Set state
                this.isFirstLoading = false
                this.isGalleryLoading = false

            }, MIN_LOADING_TIME)
        },

        /**
        * Promise to load image
        */
        async loadImages(imageArr) {
            const loadingPromisesArr = []
            let imageIndex = 0;

            while (imageIndex < imageArr.length) {
                const currentUrlToLoad = imageArr[imageIndex]
                loadingPromisesArr.push(loadImage(currentUrlToLoad))
                imageIndex++
            }
            const loadedImages = await Promise.all(loadingPromisesArr)

            // Store loaded images
            let loadedImagesIndex = 0
            while (loadedImagesIndex < loadedImages.length) {
                const currentLoadedImage = loadedImages[loadedImagesIndex]
                this.loadedImages.push({
                    id: currentLoadedImage.url,
                    imageObj: currentLoadedImage
                })
                loadedImagesIndex++
            }
        },

        /**
        * Callback on variant change
        */
        onProductVariantChange(variantId) {

            if (this.isFirstLoading) {
                this.oldVariantId = variantId
                return
            }

            // TODO: If variant doesn't exist do something
            if (!variantId) return

            // If already current variant id
            if (this.oldVariantId === variantId) return

            this.currentProductVariant = this.allProductVariants.find(variant => variant.id === variantId)

            this.updateGallery()

            this.oldVariantId = variantId
        },

        /**
        * Compare image utils
        */
        compareImagesArr(imagesArr) {
            return function(current){
                return imagesArr.filter(function(image){
                return image.id == current.id
                }).length == 0;
            }
        }
    }
}

export default productGalleryMixin;
